/**
 * `availableActions` — the complete legal move set for a state.
 *
 * Two very different consumers depend on this being exactly right. The solver
 * enumerates from it, so an omitted action makes a mission look unwinnable and
 * an illegal one makes the search explore states that cannot exist. The
 * playtest agent's responses are validated against it, so anything missing here
 * shows up as an agent "mistake" and quietly distorts the measured difficulty.
 *
 * The contract is therefore two-way: every action returned succeeds when
 * dispatched, and no legal action is omitted. Actions that would leave the state
 * untouched — reopening the channel already on screen, retargeting the node
 * already targeted — are omitted, because to the solver they are branches that
 * lead nowhere.
 *
 * The order is deterministic and follows the package's declaration order, so
 * two runs enumerate identically.
 */
import { optionsForSlot } from './composer.js';
import { harvestKey } from './pack.js';
import { bandwidthCostOf, freeBandwidth, puppetSelectable } from './possession.js';
import { canExecuteSyncPitch, canStageDraft, hintAvailable, targetable } from './reduce.js';
import { puppetIdentity } from './state.js';
export function availableActions(loaded, state) {
    if (state.end !== null)
        return [];
    const actions = [];
    // A held acquisition narrows the game to one question. Enumerating anything
    // else here would let the solver explore states the reducer refuses to reach.
    const pendingNodeId = state.bandwidth.pendingNodeId;
    if (pendingNodeId !== null) {
        for (const puppet of loaded.pack.puppets) {
            if (state.puppets[puppet.nodeId]?.possessed !== true)
                continue;
            actions.push({ type: 'RELEASE_PUPPET', nodeId: puppet.nodeId });
        }
        actions.push({ type: 'CANCEL_ACQUISITION' });
        return actions;
    }
    // Channels the player can read, other than the one already open.
    for (const channel of loaded.pack.channels) {
        if (channel.id === state.activeChannelId)
            continue;
        if (state.channels[channel.id]?.unlocked !== true)
            continue;
        actions.push({ type: 'OPEN_CHANNEL', channelId: channel.id });
    }
    // Harvestable spans in messages that are actually on screen.
    for (const message of state.messages) {
        if (state.channels[message.channelId]?.unlocked !== true)
            continue;
        for (const highlight of message.highlights) {
            if (state.harvested.includes(harvestKey(message.id, highlight.id)))
                continue;
            actions.push({ type: 'HARVEST', messageId: message.id, highlightId: highlight.id });
        }
    }
    // Every particle the picker would offer, for every active slot. Nothing is
    // filtered by whether it is a *good* idea (design D9) — only by whether it
    // would move: re-choosing the phrase already in a slot leaves the state
    // exactly as it was, the same dead branch as reopening the open channel.
    for (const slot of state.composer.slots) {
        for (const particleId of optionsForSlot(loaded, state, slot.n)) {
            if (particleId === slot.particleId)
                continue;
            actions.push({ type: 'SELECT', slot: slot.n, particleId });
        }
        if (slot.particleId !== null)
            actions.push({ type: 'CLEAR_SLOT', slot: slot.n });
    }
    if (state.composer.slots.some((slot) => slot.particleId !== null)) {
        actions.push({ type: 'CLEAR_ALL' });
    }
    // Who the assembly can be addressed to.
    for (const node of loaded.pack.cast) {
        if (node.id === state.composer.targetNodeId)
            continue;
        if (!targetable(loaded, state, node.id))
            continue;
        actions.push({ type: 'SET_TARGET', nodeId: node.id });
    }
    // Whose voice to speak in, and who to let go of.
    if (state.identity !== 'self')
        actions.push({ type: 'SWITCH_IDENTITY', identity: 'self' });
    for (const puppet of loaded.pack.puppets) {
        const identity = puppetIdentity(puppet.nodeId);
        const held = state.puppets[puppet.nodeId];
        if (held === undefined)
            continue;
        if (held.possessed)
            actions.push({ type: 'RELEASE_PUPPET', nodeId: puppet.nodeId });
        if (state.identity === identity)
            continue;
        if (!puppetSelectable(loaded, state, puppet.nodeId))
            continue;
        // An acquisition that will not fit is still legal: it opens the forced
        // release choice rather than being silently unavailable, and the solver has
        // to be able to walk that trade to find a path through it.
        const cost = bandwidthCostOf(loaded, puppet.nodeId) ?? 0;
        if (!held.possessed) {
            if (cost > state.bandwidth.capacity)
                continue;
            // …but only when the trade is one the player could actually make. With
            // nobody held there is nothing to release, so the choice would be a dead
            // end, and a dead end is a branch the solver should never be offered.
            const tradeable = state.bandwidth.used > 0;
            if (cost > freeBandwidth(state) && !tradeable)
                continue;
        }
        actions.push({ type: 'SWITCH_IDENTITY', identity });
    }
    // Staging a draft for a synchronised pitch, and letting it go.
    if (canStageDraft(loaded, state))
        actions.push({ type: 'STAGE_DRAFT' });
    for (const draft of state.staged) {
        actions.push({ type: 'UNSTAGE_DRAFT', identity: draft.identity });
    }
    if (canExecuteSyncPitch(loaded, state))
        actions.push({ type: 'EXECUTE_SYNC_PITCH' });
    // Send is blocked only structurally: an empty required slot, no target, or a
    // composer the HR ladder has locked.
    const sendable = state.composer.targetNodeId !== null &&
        state.pip.composerLockTicks === 0 &&
        state.composer.slots.every((slot) => !slot.required || slot.particleId !== null);
    if (sendable)
        actions.push({ type: 'SEND' });
    // The profile's disaster undo, while the window it opened is still open.
    if (state.undoOffer !== null)
        actions.push({ type: 'UNDO_SEND' });
    actions.push({ type: 'WAIT' });
    actions.push({ type: 'ADVANCE_PHASE' });
    if (hintAvailable(state))
        actions.push({ type: 'REQUEST_HINT' });
    return actions;
}
//# sourceMappingURL=actions.js.map